import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import ImageGalleryRenderer from "@/components/blog/ImageGalleryRenderer";

interface GalleryImage {
  src: string;
  alt?: string;
}

/**
 * Read-only ImageGallery node for the PostViewer.
 * Uses the same ImageGalleryRenderer as the editor but without edit capabilities.
 */
function ImageGalleryNodeView(props: NodeViewProps) {
  const { node } = props;
  const images = (node.attrs.images || []) as GalleryImage[];
  const className = (node.attrs.class as string) || "media-default";

  return (
    <NodeViewWrapper as="div" className="image-gallery-wrapper">
      <ImageGalleryRenderer images={images} className={className} />
    </NodeViewWrapper>
  );
}

export const ImageGalleryNode = Node.create({
  name: "imageGallery",

  group: "block",

  atom: true,

  draggable: false,

  selectable: false,

  addAttributes() {
    return {
      images: {
        default: [],
        parseHTML: (element) => {
          const imagesData = element.getAttribute("data-images");
          try {
            return imagesData ? JSON.parse(imagesData) : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attributes) => {
          return { "data-images": JSON.stringify(attributes.images) };
        },
      },
      class: {
        default: "media-default",
        parseHTML: (element) => element.getAttribute("data-class") || "media-default",
        renderHTML: (attributes) => {
          return { "data-class": attributes.class };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "figure[data-type='image-gallery']",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const images = HTMLAttributes.images || [];

    return [
      "figure",
      mergeAttributes({
        "data-type": "image-gallery",
        "data-images": JSON.stringify(images),
        "data-class": HTMLAttributes.class,
        class: `image-gallery ${HTMLAttributes.class || "media-default"}`,
      }),
      [
        "div",
        { class: "gallery-container" },
        ...images.map((img: GalleryImage, index: number) => [
          "img",
          {
            src: img.src,
            alt: img.alt || "",
            class: "gallery-image",
            "data-index": index.toString(),
          },
        ]),
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageGalleryNodeView);
  },
});

export default ImageGalleryNode;
